import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, CheckCircle, Clock, Eye, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const CHECK_NAMES = {
  'address_check': 'Address',
  'criminal_court_check': 'Criminal Court',
  'id_check': 'ID Check',
  'employment_check': 'Employment',
  'education_check': 'Education',
  'credit_check': 'Credit',
  'personal_reference': 'Personal Ref',
  'professional_reference': 'Professional Ref'
};

export default function InsufficiencyTab({ clientId, onViewCase }) {
  const [insufficiencies, setInsufficiencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({ open: 0, closed: 0, avgTat: 0 });

  useEffect(() => {
    loadInsufficiencies();
  }, [clientId, statusFilter]);

  const loadInsufficiencies = async () => {
    try {
      let url = '/insufficiencies/consolidated';
      const params = [];
      if (clientId) params.push(`client_id=${clientId}`);
      if (statusFilter !== 'all') params.push(`status=${statusFilter}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await axios.get(url);
      const data = response.data || [];
      setInsufficiencies(data);
      
      // Calculate stats
      const open = data.filter(i => i.status === 'open').length;
      const closed = data.filter(i => i.status === 'closed').length;
      const tats = data.filter(i => i.cleared_tat_days !== null).map(i => i.cleared_tat_days);
      const avgTat = tats.length > 0 ? Math.round(tats.reduce((a, b) => a + b, 0) / tats.length) : 0;
      
      setStats({ open, closed, avgTat });
    } catch (error) {
      toast.error('Failed to load insufficiencies');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatDateForExport = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  const exportToCSV = () => {
    if (insufficiencies.length === 0) {
      toast.error('No data to export');
      return;
    }

    // CSV Headers as per user request
    const headers = [
      'Case ID',
      'Submitted Date',
      'Initiated Date',
      'Candidate Name',
      'Entity Name',
      'Check Name',
      'Check Status',
      'Insufficiency Reason',
      'Insufficiency Details',
      'Date Raised',
      'Date Cleared',
      'TAT (Days)',
      'Status'
    ];

    // Build CSV rows
    const rows = insufficiencies.map(insuff => [
      insuff.case_id || '',
      formatDateForExport(insuff.case_submitted_date),
      formatDateForExport(insuff.case_initiated_date || insuff.date_raised),
      insuff.employee_name || '',
      insuff.entity_name || insuff.client_name || '',
      CHECK_NAMES[insuff.check_type] || insuff.check_type || '',
      insuff.check_status || '',
      (insuff.reason || '').replace(/_/g, ' '),
      (insuff.insufficiency_detail || '').replace(/"/g, '""'),
      formatDateForExport(insuff.date_raised),
      formatDateForExport(insuff.date_cleared),
      insuff.cleared_tat_days !== null ? insuff.cleared_tat_days : '',
      insuff.status || ''
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `insufficiency_report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${insufficiencies.length} insufficiency records`);
  };

  if (loading) {
    return <div className="p-8 text-center">Loading insufficiencies...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Insufficiency Management</h2>
          <p className="text-slate-600">Track and manage all insufficiencies raised</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={exportToCSV}
            disabled={insufficiencies.length === 0}
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            data-testid="export-insufficiency-csv"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-rose-600 font-medium">Open Insufficiencies</p>
                <p className="text-3xl font-bold text-rose-700">{stats.open}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-rose-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600 font-medium">Cleared Insufficiencies</p>
                <p className="text-3xl font-bold text-emerald-700">{stats.closed}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Avg. Resolution TAT</p>
                <p className="text-3xl font-bold text-blue-700">{stats.avgTat} days</p>
              </div>
              <Clock className="w-10 h-10 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Insufficiency List</CardTitle>
          <p className="text-sm text-slate-500">View all open insufficiencies across cases</p>
        </CardHeader>
        <CardContent>
          {insufficiencies.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No insufficiencies found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Case ID</TableHead>
                    <TableHead>Client / Entity</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Check Type</TableHead>
                    <TableHead>Reason & Details</TableHead>
                    <TableHead>Raised On</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {insufficiencies.map((insuff) => (
                    <TableRow key={insuff.insufficiency_id}>
                      <TableCell>
                        <p className="font-mono text-sm font-medium">{insuff.case_id}</p>
                        <p className="text-xs text-slate-400">Check: {insuff.check_id || '-'}</p>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-slate-800">{insuff.client_name || '-'}</p>
                        <p className="text-xs text-slate-500">{insuff.entity_name || '-'}</p>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{insuff.employee_name}</p>
                        <p className="text-xs text-slate-500">{insuff.employee_email}</p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{CHECK_NAMES[insuff.check_type] || insuff.check_type}</Badge>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <p className="font-semibold text-rose-600">{insuff.reason?.replace(/_/g, ' ').toUpperCase()}</p>
                        <p className="text-sm text-slate-600 line-clamp-2">{insuff.insufficiency_detail || '-'}</p>
                        {insuff.candidate_response && (
                          <p className="text-xs text-blue-600 mt-1 italic">Response: {insuff.candidate_response}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{formatDate(insuff.date_raised)}</p>
                        {insuff.date_cleared && (
                          <p className="text-xs text-emerald-600">Cleared: {formatDate(insuff.date_cleared)}</p>
                        )}
                        {insuff.cleared_tat_days !== null && (
                          <Badge className={insuff.cleared_tat_days <= 2 ? 'bg-emerald-100 text-emerald-700 mt-1' : 'bg-amber-100 text-amber-700 mt-1'}>
                            {insuff.cleared_tat_days} days
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          insuff.status === 'open' ? 'bg-rose-100 text-rose-700' : 
                          insuff.status === 'waiting_for_clearance' ? 'bg-amber-100 text-amber-700' :
                          'bg-emerald-100 text-emerald-700'
                        }>
                          {insuff.status === 'waiting_for_clearance' ? 'PENDING REVIEW' : insuff.status?.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => onViewCase && onViewCase(insuff.case_id, insuff.check_id)}
                        >
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
